import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent,
  type KeyboardEvent,
  type RefObject,
} from "react";
import gsap from "gsap";

import { useReducedMotion } from "@/shared/motion";

import {
  BALL_SIZE,
  DEFAULT_DOCK,
  fromDrop,
  loadDock,
  nudge,
  saveDock,
  toPixels,
  type Dock,
} from "./dockPosition";
import type { RobotMotion } from "./useRobotMotion";
import { getPersona } from "@/shared/brand/persona";

/** 位移超过此阈值(px)才算拖动;之内算点击。防止手抖把「点开军师」变成「挪了一下」。 */
const DRAG_THRESHOLD = 4;

interface Options {
  ref: RefObject<HTMLElement>;
  /** 真正的点击(非拖动)→ 开抽屉。 */
  onTap: () => void;
  motion: RobotMotion;
  /**
   * 拖动起止上报。dragging 由调用方持有而非本 hook —— 否则 mode(要 dragging)→ motion → dock
   * (产出 dragging)成环。
   */
  onDraggingChange?: (dragging: boolean) => void;
}

export interface LauncherDock {
  dock: Dock;
  /** 给 aria-live 播报的文案(拖动是视觉动作,读屏用户需要口播)。 */
  announcement: string;
  /** ContextMenu / 键盘用的显式改位。 */
  moveTo: (next: Dock) => void;
  resetDock: () => void;
  handlers: {
    onPointerDown: (e: PointerEvent<HTMLElement>) => void;
    onPointerMove: (e: PointerEvent<HTMLElement>) => void;
    onPointerUp: (e: PointerEvent<HTMLElement>) => void;
    onPointerCancel: (e: PointerEvent<HTMLElement>) => void;
    onPointerLeave: () => void;
    onKeyDown: (e: KeyboardEvent<HTMLElement>) => void;
    onClick: () => void;
  };
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/**
 * 播报文案(读屏用户唯一的位置反馈)。**竖向必须进文案**:Alt+↑/↓ 只改 offsetY 不改 side,
 * 旧版只播报左右 → setAnnouncement 设的是同一个字符串 → DOM 文本没变 → aria-live 不重播 →
 * 按 Alt+↑ 什么都听不到。而键盘路径恰恰就是给非鼠标用户准备的,哑掉等于这一半没做
 * (2026-07-15 生产走查:Alt+↑ 后播报仍停在「军师已靠右」)。
 *
 * <p>用百分比而非「靠上/居中/靠下」分档:步长 0.1,同档内连按仍是同一串 → 会退化回哑的。
 * 百分比保证每挪一步文案必变,aria-live 才会逐次播。
 */
const positionLabel = (d: Dock) =>
  `${getPersona().title}已${d.side === "left" ? "靠左" : "靠右"},距顶 ${Math.round(clamp(d.offsetY, 0, 1) * 100)}%`;

/**
 * 把落点钳进视口。上界用 max(0, ...):视口比球还小(极窄 iframe / DevTools 拉到 <56px)时
 * `innerWidth - BALL_SIZE` 为负,裸 clamp(v, 0, 负数) 会**返回负数**把球推出屏外(codex 红队 #4)。
 */
function clampToViewport(x: number, y: number): { x: number; y: number } {
  return {
    x: clamp(x, 0, Math.max(0, window.innerWidth - BALL_SIZE)),
    y: clamp(y, 0, Math.max(0, window.innerHeight - BALL_SIZE)),
  };
}

/**
 * 悬浮球拖动 + 吸边 + 持久化 + 键盘等价路径。
 *
 * <p>拖动中**不 setState**:每帧直接写 transform(gsap.set),否则 60fps 重渲染整棵树。
 * 只有松手落位才进 React + 落 localStorage。
 *
 * <p>click vs drag:位移 ≤4px 算点击(开抽屉),>4px 算拖动且抑制随后的 click。键盘 Enter/Space
 * 走的是 click(无 pointer 序列)→ 不会被抑制,照常开抽屉。
 */
export function useLauncherDock({ ref, onTap, motion, onDraggingChange }: Options): LauncherDock {
  const reduced = useReducedMotion();
  const [dock, setDock] = useState<Dock>(loadDock);
  const [announcement, setAnnouncement] = useState("");

  const dockRef = useRef(dock);
  dockRef.current = dock;
  const onDrag = useRef(onDraggingChange);
  onDrag.current = onDraggingChange;
  const mounted = useRef(false);
  const st = useRef({
    active: false,
    /** 正在拖的那根手指/笔;其余 pointer 的事件一律忽略(codex 红队 #2:第二指会顶掉第一指的状态)。 */
    pid: -1,
    moved: false,
    sx: 0,
    sy: 0,
    ox: 0,
    oy: 0,
    lastX: 0,
    lastT: 0,
    suppressClick: false,
  });

  /** 把 dock 落到元素上;animate=false 用于首帧与 resize(此时不该有动画)。 */
  const apply = useCallback(
    (d: Dock, animate: boolean) => {
      const el = ref.current;
      if (!el) return;
      const { x, y } = toPixels(d, window.innerWidth, window.innerHeight);
      // 先杀掉 root 上在跑的旧位移 tween:0.42s 吸附动画未完就 resize 时,resize 只 gsap.set 新位置,
      // 旧 tween 会继续把球写回旧坐标(codex 红队 #3)。root 的 transform 只有停靠位在写(见
      // useRobotMotion 的分层说明),所以整体 kill 是安全的,不会误伤机器人动效。
      gsap.killTweensOf(el);
      // reduced-motion 下吸附瞬移:拖动是用户直接操纵,不响应才是 bug —— 但不该有装饰性缓动。
      if (animate && !reduced) gsap.to(el, { x, y, duration: 0.42, ease: "back.out(1.4)" });
      else gsap.set(el, { x, y });
    },
    [ref, reduced]
  );

  // dock 变化 → 落位(首帧不animate,之后的变化都是用户动作,animate)
  useEffect(() => {
    apply(dock, mounted.current);
    mounted.current = true;
  }, [dock, apply]);

  // resize/缩放 → 按比例重算像素(dock 本身不变,所以不写库)
  useEffect(() => {
    const on = () => apply(dockRef.current, false);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, [apply]);

  const commit = useCallback((next: Dock) => {
    setDock(next);
    saveDock(next);
    setAnnouncement(positionLabel(next));
  }, []);

  const onPointerDown = useCallback(
    (e: PointerEvent<HTMLElement>) => {
      if (e.button !== 0) return; // 右键留给系统菜单
      // 已经有一根手指在拖 → 忽略后来的手指(多指同按会顶掉第一指的起点,球乱跳)
      if (st.current.active) return;
      // 球正处在吸附动画中被抓住 → 先停下,否则 tween 会和拖动抢着写 transform
      if (ref.current) gsap.killTweensOf(ref.current);
      const { x, y } = toPixels(dockRef.current, window.innerWidth, window.innerHeight);
      st.current = {
        active: true,
        pid: e.pointerId,
        moved: false,
        sx: e.clientX,
        sy: e.clientY,
        ox: x,
        oy: y,
        lastX: e.clientX,
        lastT: e.timeStamp,
        suppressClick: false,
      };
      // jsdom 无 Pointer Capture 实现 → 可选调用
      ref.current?.setPointerCapture?.(e.pointerId);
    },
    [ref]
  );

  const onPointerMove = useCallback(
    (e: PointerEvent<HTMLElement>) => {
      const s = st.current;
      const el = ref.current;
      if (!el) return;
      if (!s.active) {
        // 悬停时眼睛看向光标(只在球上监听,不挂全局 mousemove)
        const r = el.getBoundingClientRect();
        motion.lookAt(e.clientX - (r.left + r.width / 2), e.clientY - (r.top + r.height / 2));
        return;
      }
      if (e.pointerId !== s.pid) return; // 不是正在拖的那根手指
      const dx = e.clientX - s.sx;
      const dy = e.clientY - s.sy;
      if (!s.moved) {
        if (Math.hypot(dx, dy) <= DRAG_THRESHOLD) return;
        s.moved = true;
        onDrag.current?.(true);
      }
      gsap.set(el, clampToViewport(s.ox + dx, s.oy + dy));
      // 横向速度 → 车身倾斜(px/帧)
      const dt = e.timeStamp - s.lastT;
      if (dt > 0) motion.tilt(((e.clientX - s.lastX) / dt) * 16);
      s.lastX = e.clientX;
      s.lastT = e.timeStamp;
    },
    [ref, motion]
  );

  const finish = useCallback(
    (e: PointerEvent<HTMLElement>, commitDrop: boolean) => {
      const s = st.current;
      if (!s.active || e.pointerId !== s.pid) return; // 不是正在拖的那根手指
      s.active = false;
      s.pid = -1;
      ref.current?.releasePointerCapture?.(e.pointerId);
      motion.tilt(0);
      if (!s.moved) return; // 点击:交给 onClick
      onDrag.current?.(false);
      s.suppressClick = true; // 拖完那一下 click 不该开抽屉
      if (!commitDrop) {
        apply(dockRef.current, true); // 取消 → 弹回原位
        return;
      }
      const { x, y } = clampToViewport(s.ox + (e.clientX - s.sx), s.oy + (e.clientY - s.sy));
      const next = fromDrop(x, y, window.innerWidth, window.innerHeight);
      // dock 值可能与当前相同(原地小挪)→ useEffect 不触发,这里显式补一次吸附动画
      if (next.side === dockRef.current.side && next.offsetY === dockRef.current.offsetY) {
        apply(next, true);
      }
      commit(next);
    },
    [ref, motion, apply, commit]
  );

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLElement>) => {
      // 拖动是鼠标专属 → 键盘等价路径:Alt+方向键
      if (!e.altKey) return;
      const dir = (
        { ArrowLeft: "left", ArrowRight: "right", ArrowUp: "up", ArrowDown: "down" } as const
      )[e.key as "ArrowLeft" | "ArrowRight" | "ArrowUp" | "ArrowDown"];
      if (!dir) return;
      e.preventDefault();
      commit(nudge(dockRef.current, dir));
    },
    [commit]
  );

  const onClick = useCallback(() => {
    if (st.current.suppressClick) {
      st.current.suppressClick = false;
      return;
    }
    onTap();
  }, [onTap]);

  return {
    dock,
    announcement,
    moveTo: commit,
    resetDock: useCallback(() => commit(DEFAULT_DOCK), [commit]),
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: useCallback((e: PointerEvent<HTMLElement>) => finish(e, true), [finish]),
      onPointerCancel: useCallback((e: PointerEvent<HTMLElement>) => finish(e, false), [finish]),
      onPointerLeave: useCallback(() => {
        if (!st.current.active) motion.lookAway();
      }, [motion]),
      onKeyDown,
      onClick,
    },
  };
}
